import type { AppProps } from 'next/app';
import { ReactElement, ReactNode, useEffect } from 'react';
import { NextPage } from 'next';
import Analytics from 'analytics';
import { AnalyticsProvider } from 'use-analytics';
import { Box, ChakraProvider } from '@chakra-ui/react';
import googleAnalytics from '@analytics/google-analytics';
import mixpanelPlugin from '@analytics/mixpanel';
import { Inter } from 'next/font/google';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { theme } from '@/styles/theme';
import '@/styles/theme/global.css';
import { analyticsDataDogPlugin } from '@/modules/analytics/data-dog-rum';
import { AuthProvider } from '@/modules/Auth';
import NetworkErrorBoundary from '@/modules/error/network-error';
import Axios from 'axios';
import { RefreshProvider } from '@/modules/baseAPI/refresh-provider';
import { ClientErrorBoundary } from '../modules/error/client-error-boundary';
import { AlertProvider } from '@/modules/utils/modals/alert/AlertProvider';
import { datadogRum } from '@datadog/browser-rum';
import { useRouter } from 'next/router';
import { AppScope } from '@/modules/app-scope/constants/app-scope.enum';
import { useAppScope } from '@/modules/app-scope/context/use-app-scope';
import { withAppScopeProvider } from '@/modules/app-scope/context/app-scope-provider';
import { CaAuthProvider } from '@/modules/CA/auth/context/ca-auth-provider';
import { CaCookiesProvider } from '@/modules/CA/cookies/context/ca-cookies-provider';
import { CaOrganizationProvider } from '@/modules/CA/organization/context/ca-organization-provider';
import { CaUserProvider } from '@/modules/CA/user/context/ca-user-provider';
import { CaSWRConfig } from '@/modules/CA/swr-config/ca-swr-config';
import { CaApiSdkProvider } from '@/modules/CA/api-sdk/context/ca-api-sdk-provider';
import { IntercomProvider } from 'react-use-intercom';

Axios.defaults.baseURL = process.env.NEXT_PUBLIC_API_DOMAIN;

// If loading a variable font, you don't need to specify the font weight
const inter = Inter({ subsets: ['latin'] });

export type NextPageWithLayout<P = object, IP = P> = NextPage<P, IP> & {
  getLayout?: (page: ReactElement) => ReactNode;
};

export type AppPropsWithLayout = AppProps & {
  Component: NextPageWithLayout;
};

const mixpanelPluginInstance = process.env.NEXT_PUBLIC_MIXPANEL_TOKEN
  ? [
      mixpanelPlugin({
        token: process.env.NEXT_PUBLIC_MIXPANEL_TOKEN || '',
        options: {
          debug: false,
          track_pageview: true,
          persistence: 'localStorage',
        },
      }),
    ]
  : [];

const analytics = Analytics({
  app: 'ny-ui',
  plugins: [
    ...mixpanelPluginInstance,
    analyticsDataDogPlugin({
      applicationId: process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID,
      clientToken: process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN,
      env: process.env.NEXT_PUBLIC_ENV,
      version: process.env.NEXT_PUBLIC_VERSION,
    }),
    googleAnalytics({
      measurementIds: [process.env.NEXT_PUBLIC_GOOGLE_ANALYTICS_TRACKING_ID],
    }),
  ],
});

console.log('starting frontend Nabis application with variables:');
console.table([
  ['NEXT_PUBLIC_DATADOG_APPLICATION_ID', process.env.NEXT_PUBLIC_DATADOG_APPLICATION_ID],
  ['NEXT_PUBLIC_DATADOG_CLIENT_TOKEN', process.env.NEXT_PUBLIC_DATADOG_CLIENT_TOKEN],
  ['NEXT_PUBLIC_ENV', process.env.NEXT_PUBLIC_ENV],
  ['NEXT_PUBLIC_API_DOMAIN', process.env.NEXT_PUBLIC_API_DOMAIN],
  ['NEXT_PUBLIC_GOOGLE_AUTH_CLIENT_ID', process.env.NEXT_PUBLIC_GOOGLE_AUTH_CLIENT_ID],
  ['NEXT_PUBLIC_VERSION', process.env.NEXT_PUBLIC_VERSION],
  ['NEXT_PUBLIC_IMGIX_SUBDOMAIN', process.env.NEXT_PUBLIC_IMGIX_SUBDOMAIN],
]);

function App({ Component, pageProps }: AppPropsWithLayout) {
  // Use the layout defined at the page level, if available
  const getLayout = Component.getLayout ?? ((page) => page);
  const router = useRouter();

  useEffect(() => {
    // Report views for unique pathname/query/route combos
    // also shows view name with variable names (ex: /organizations/[org]/orders)
    // full pathname will be the
    datadogRum.startView({
      name: router.pathname,
    });
  }, [router.pathname, router.query, router.route]);

  const { appScope } = useAppScope();

  /**
   * isolate the code for redirected-from-CA from the rest of the app.
   * this way, we can keep the rest of the app clean until we converge the two code paths.
   * convergence should be much easier once the CA and NY DB models are unified. for example, instead
   * of complicating the existing `AuthProvider` with a bunch of conditional logic, we can create separate
   * providers and only use those when the app is redirected from CA. the CA part of the app is completely
   * unaware of the rest of the app, so we can make changes to it without worrying about breaking the NY part.
   */
  if (appScope === AppScope.CA) {
    return (
      <>
        <Box
          as="main"
          className={inter.className}
          // for `ClientErrorBoundary` to center vertically
          minHeight="100vh"
          position="relative"
        >
          <ChakraProvider theme={theme}>
            <ClientErrorBoundary>
              <AnalyticsProvider instance={analytics}>
                <CaSWRConfig>
                  <CaCookiesProvider>
                    <CaApiSdkProvider>
                      <CaUserProvider>
                        <CaOrganizationProvider>
                          <CaAuthProvider>
                            <AlertProvider>{getLayout(<Component {...pageProps} />)}</AlertProvider>
                          </CaAuthProvider>
                        </CaOrganizationProvider>
                      </CaUserProvider>
                    </CaApiSdkProvider>
                  </CaCookiesProvider>
                </CaSWRConfig>
              </AnalyticsProvider>
            </ClientErrorBoundary>
          </ChakraProvider>
        </Box>
      </>
    );
  }

  return (
    <>
      <Box
        as="main"
        className={inter.className}
        // for `ClientErrorBoundary` to center vertically
        minHeight="100vh"
        position="relative"
      >
        <ChakraProvider theme={theme}>
          <ClientErrorBoundary>
            <AnalyticsProvider instance={analytics}>
              <NetworkErrorBoundary>
                <IntercomProvider appId={process.env.NEXT_PUBLIC_INTERCOM_APP_ID ?? ''}>
                  <AuthProvider>
                    <RefreshProvider>
                      <GoogleOAuthProvider clientId={process.env.NEXT_PUBLIC_GOOGLE_AUTH_CLIENT_ID as string}>
                        <AlertProvider>{getLayout(<Component {...pageProps} />)}</AlertProvider>
                      </GoogleOAuthProvider>
                    </RefreshProvider>
                  </AuthProvider>
                </IntercomProvider>
              </NetworkErrorBoundary>
            </AnalyticsProvider>
          </ClientErrorBoundary>
        </ChakraProvider>
      </Box>
    </>
  );
}

export default withAppScopeProvider(App);
